import { ComponentPropsWithoutRef } from 'react'

import { useRouter } from 'next/router'

import useSWR from 'swr'

import { Article, ArticleChildrenCarouselDocument } from '@/generated/contentful'

import { client } from '@/lib/contentful/client'
import { serializeCacheKey, useSlug } from '@/lib/contentful/utils'

import { CardCarousel } from '@/components/CardCarousel'

type Props = Omit<ComponentPropsWithoutRef<typeof CardCarousel>, 'cards'> & { ctaText?: string }

export function ArticleChildrenCarousel(props: Props) {
	const slug = useSlug()
	const { locale, isPreview } = useRouter()
	const { data } = useSWR(serializeCacheKey('articleChildren', { slug, locale }), () =>
		client({ preview: Boolean(isPreview) }).request(ArticleChildrenCarouselDocument, {
			where: { parentArticle: { slug }, editStatus: '🟢 live' },
			locale,
			preview: Boolean(isPreview)
		})
	)
	const { data: parentArticleCard } = useSWR<Article & { anchorLink: string }>(
		serializeCacheKey('parentArticleCard', { slug, locale })
	)
	if (!data?.articleCollection?.items) return null

	const cards = [...(parentArticleCard ? [parentArticleCard] : []), ...data.articleCollection.items]

	return (
		<CardCarousel
			{...props}
			dataTestId="article-children-carousel"
			cards={cards.map((item) => {
				const isRoot = !item?.parentArticle?.slug
				const href = isRoot
					? `/articles/${item?.slug}`
					: `/articles/${item?.parentArticle?.slug}/${item?.slug}`
				return {
					heading: 'articles',
					linkText: props.ctaText || 'Read article',
					imageAlt: item?.featuredImage?.title ?? `${item?.title} image`,
					imageSrc: {
						url: item?.featuredImage?.url ?? '',
						dimensions: {
							width: item?.featuredImage?.width ?? 200,
							height: item?.featuredImage?.height ?? 200
						}
					},
					title: item?.title ?? '',
					text: item?.cardSummary ?? item?.seoMetadata?.description ?? '',
					linkURL: { href, target: '_self' },
					anchorLink: item && 'anchorLink' in item ? item.anchorLink : ''
				}
			})}
		/>
	)
}
